<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div v-if="!canManageBookings" class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="alert alert-warning mb-0">
              You do not have permission to view appointment bookings for this business.
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-else>
      <div class="row">
        <div class="col-12">
          <b-tabs v-model="activeBookingsTab" pills nav-class="p-0 bookings-tabs mb-3" content-class="text-dark">
            <b-tab title="Appointments" class="border-0"></b-tab>
            <b-tab title="Booking Calendar" class="border-0"></b-tab>
          </b-tabs>
        </div>

        <div v-if="activeBookingsTab === 0" class="col-12">
          <div class="row g-3 mb-3">
            <div class="col-xl-3 col-md-6" v-for="card in summaryCards" :key="card.key">
              <div class="card summary-card">
                <div class="card-body">
                  <span class="summary-label">{{ card.label }}</span>
                  <div class="summary-value">{{ card.value }}</div>
                  <div class="summary-note">{{ card.note }}</div>
                </div>
              </div>
            </div>
          </div>

          <div class="card bookings-review-card">
            <div class="card-body">
              <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div>
                  <h4 class="card-title mb-1">Appointments</h4>
                  <p class="text-muted mb-0">Review booked slots, confirm manual requests, and move patients into the normal Afrimed visit flow.</p>
                </div>
                <div class="booking-tools">
                  <b-form-input
                    v-model.trim="searchTerm"
                    placeholder="Search patient, service, doctor, status..."
                    class="booking-search"
                  ></b-form-input>
                  <multiselect
                    v-model="statusFilter"
                    :options="statusFilterOptions"
                    track-by="value"
                    label="label"
                    :searchable="false"
                    :close-on-select="true"
                    :allow-empty="false"
                    :show-labels="false"
                    placeholder="All statuses"
                    class="booking-status-select"
                  ></multiselect>
                  <span class="badge bg-soft-warning text-warning pending-pill">Pending {{ pendingVisibleCount }}</span>
                </div>
              </div>

              <div v-if="loading" class="bookings-state">
                <b-spinner class="align-middle me-1" small></b-spinner>
                <strong>Loading appointment bookings...</strong>
              </div>

              <div v-else-if="!bookings.length" class="bookings-state empty">
                No appointment bookings match the current view.
              </div>

              <div v-else class="bookings-table-shell">
                <b-table
                  :items="bookings"
                  :fields="bookingFields"
                  responsive="lg"
                  hover
                  thead-class="bookings-head"
                  tbody-tr-class="booking-row"
                  @row-clicked="openBookingDetail"
                >
                  <template #cell(patient_name)="data">
                    <div class="booking-primary-cell">
                      <strong class="booking-link-text">{{ data.item.patient_name }}</strong>
                      <span>{{ data.item.patient_contact || data.item.patient_email || "No contact provided" }}</span>
                    </div>
                  </template>

                  <template #cell(service_name)="data">
                    <div class="booking-primary-cell">
                      <strong>{{ data.item.service_name || "Unknown service" }}</strong>
                      <span>{{ data.item.service_category_name || "No category" }}</span>
                    </div>
                  </template>

                  <template #cell(doctor_name)="data">
                    <div>
                      <strong>{{ data.item.doctor_name || "Unassigned doctor" }}</strong>
                    </div>
                  </template>

                  <template #cell(slot)="data">
                    <div class="booking-slot-cell">
                      <strong>{{ formatDate(data.item.slot_date) }}</strong>
                      <span>{{ formatTimeRange(data.item.start_time, data.item.end_time) }}</span>
                    </div>
                  </template>

                  <template #cell(payment)="data">
                    <div class="booking-slot-cell">
                      <strong>{{ formatPaymentMode(data.item.payment_mode) }}</strong>
                      <span>{{ money(data.item.price) }}</span>
                    </div>
                  </template>

                  <template #cell(status)="data">
                    <div class="booking-status-cell">
                      <span class="badge" :class="statusBadgeClass(data.item.status)">{{ formatStatus(data.item.status) }}</span>
                      <small v-if="data.item.payment_status" class="text-muted">{{ formatStatus(data.item.payment_status) }}</small>
                    </div>
                  </template>

                  <template #cell(actions)="data">
                    <div class="booking-actions" @click.stop>
                      <b-button size="sm" variant="outline-primary" @click="openBookingDetail(data.item)">View</b-button>
                      <b-button
                        v-if="data.item.status === 'pending_approval'"
                        size="sm"
                        variant="success"
                        @click="openApproveDialog(data.item)"
                      >
                        Approve
                      </b-button>
                      <b-button
                        v-if="data.item.status === 'pending_approval'"
                        size="sm"
                        variant="danger"
                        @click="openRejectModal([data.item.id], data.item.patient_name)"
                      >
                        Reject
                      </b-button>
                      <b-button
                        v-if="data.item.status === 'approved' && !data.item.linked_visit_id"
                        size="sm"
                        variant="primary"
                        @click="openCheckInDialog(data.item)"
                      >
                        Check In
                      </b-button>
                    </div>
                  </template>
                </b-table>
              </div>

              <div class="table-footer" v-if="pagination.total">
                <div class="small text-muted">
                  Showing {{ paginationLabel }}
                </div>
                <b-pagination
                  v-model="currentPage"
                  :total-rows="pagination.total"
                  :per-page="perPage"
                  pills
                ></b-pagination>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="col-12">
          <div class="card calendar-shell-card">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between mb-3 gap-2 flex-wrap">
                <div>
                  <h4 class="card-title mb-1">Booking Calendar</h4>
                  <p class="text-muted mb-0">See hospital-wide booked appointments by day and open the selected day’s schedule.</p>
                </div>
                <div class="calendar-controls">
                  <b-button size="sm" variant="light" @click="shiftMonth(-1)">
                    <i class="mdi mdi-chevron-left"></i>
                  </b-button>
                  <span class="fw-medium">{{ calendarLabel }}</span>
                  <b-button size="sm" variant="light" @click="shiftMonth(1)">
                    <i class="mdi mdi-chevron-right"></i>
                  </b-button>
                </div>
              </div>

              <div class="calendar-shell">
                <div class="calendar-pane">
                  <div class="calendar-grid">
                    <div v-for="weekday in weekDays" :key="weekday" class="calendar-head">{{ weekday }}</div>
                    <button
                      v-for="day in calendarCells"
                      :key="day.key"
                      type="button"
                      class="calendar-cell"
                      :class="{ muted: !day.inMonth, active: selectedCalendarDate === day.date, today: day.date === todayIso, touched: day.count > 0, booked: day.pending > 0 }"
                      @click="selectCalendarDate(day.date)"
                    >
                      <span class="day-number">{{ day.day }}</span>
                      <span v-if="day.count" class="calendar-count">{{ day.count }}</span>
                      <span v-if="day.pending" class="calendar-booked-dot"></span>
                    </button>
                  </div>
                </div>

                <div class="slots-pane">
                  <div class="slots-pane-head">
                    <div>
                      <h5 class="mb-1">Day Appointments</h5>
                      <small class="text-muted">{{ selectedCalendarDateLabel }}</small>
                    </div>
                    <span v-if="selectedDayPendingCount" class="badge bg-soft-warning text-warning">
                      {{ selectedDayPendingCount }} pending
                    </span>
                  </div>

                  <div v-if="loadingDayBookings" class="slots-state">
                    <b-spinner small class="align-middle me-1"></b-spinner>
                    <strong>Loading day appointments...</strong>
                  </div>

                  <div v-else-if="!dayBookings.length" class="slots-state empty">
                    Select a touched day to view booked appointments.
                  </div>

                  <div v-else class="slot-list">
                    <button
                      v-for="booking in dayBookings"
                      :key="booking.id"
                      type="button"
                      class="slot-card booking-card-button"
                      @click="openBookingDetail(booking)"
                    >
                      <div class="d-flex justify-content-between align-items-start gap-2">
                        <div>
                          <strong>{{ booking.service_name }}</strong>
                          <div class="small text-muted">{{ booking.patient_name }}</div>
                          <div class="small text-muted">{{ booking.doctor_name || "Unassigned doctor" }}</div>
                        </div>
                        <span class="badge" :class="statusBadgeClass(booking.status)">
                          {{ formatStatus(booking.status) }}
                        </span>
                      </div>
                      <div class="small text-muted mt-2">{{ formatTimeRange(booking.start_time, booking.end_time) }}</div>
                      <div class="small text-muted">{{ formatPaymentMode(booking.payment_mode) }} • {{ money(booking.price) }}</div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <b-modal
      id="appointment-booking-detail-modal"
      centered
      size="lg"
      title="Appointment Details"
      modal-class="bookings-modal-shell booking-detail-shell"
      body-class="booking-detail-body"
    >
      <div v-if="detailBooking.id" class="booking-detail-scroll">
        <div class="booking-detail-modal">
        <div class="booking-detail-hero">
          <div>
            <span class="detail-eyebrow">{{ detailBooking.patient_name }}</span>
            <h4 class="mb-1">{{ detailBooking.service_name || "Appointment Service" }}</h4>
            <p class="text-muted mb-0">{{ detailBooking.service_category_name || "No category assigned" }}</p>
          </div>
          <span class="badge" :class="statusBadgeClass(detailBooking.status)">{{ formatStatus(detailBooking.status) }}</span>
        </div>

        <div class="booking-detail-grid">
          <div class="detail-stat">
            <div class="detail-label">Doctor</div>
            <div class="detail-value">{{ detailBooking.doctor_name || "Unassigned doctor" }}</div>
          </div>
          <div class="detail-stat">
            <div class="detail-label">Slot</div>
            <div class="detail-value">{{ formatDate(detailBooking.slot_date) }} | {{ formatTimeRange(detailBooking.start_time, detailBooking.end_time) }}</div>
          </div>
          <div class="detail-stat">
            <div class="detail-label">Payment</div>
            <div class="detail-value">{{ formatPaymentMode(detailBooking.payment_mode) }} • {{ formatStatus(detailBooking.payment_status) }}</div>
          </div>
          <div class="detail-stat">
            <div class="detail-label">Booked</div>
            <div class="detail-value">{{ formatDateTime(detailBooking.booked_at || detailBooking.created_at) || "Not available" }}</div>
          </div>
        </div>

        <div class="detail-section">
          <h6>Patient Contact</h6>
          <div class="detail-copy">
            <div><strong>Name:</strong> {{ detailBooking.patient_name }}</div>
            <div><strong>Gender:</strong> {{ detailBooking.patient_gender || "Not provided" }}</div>
            <div><strong>Date of Birth:</strong> {{ detailBooking.patient_date_of_birth ? formatDate(detailBooking.patient_date_of_birth) : "Not provided" }}</div>
            <div><strong>Contact:</strong> {{ detailBooking.patient_contact || "Not provided" }}</div>
            <div><strong>Email:</strong> {{ detailBooking.patient_email || "Not provided" }}</div>
            <div v-if="detailBooking.patient_id"><strong>Patient ID:</strong> {{ detailBooking.patient_id }}</div>
          </div>
        </div>

        <div class="detail-section">
          <h6>Workflow Status</h6>
          <div class="detail-copy">
            <div v-if="detailBooking.approved_at"><strong>Approved:</strong> {{ formatDateTime(detailBooking.approved_at) }} by {{ detailBooking.approved_by_name || "Manager" }}</div>
            <div v-if="detailBooking.rejected_at"><strong>Rejected:</strong> {{ formatDateTime(detailBooking.rejected_at) }} by {{ detailBooking.rejected_by_name || "Manager" }}</div>
            <div v-if="detailBooking.checked_in_at"><strong>Checked In:</strong> {{ formatDateTime(detailBooking.checked_in_at) }} by {{ detailBooking.checked_in_by_name || "Manager" }}</div>
            <div v-if="detailBooking.linked_visit_id"><strong>Linked Visit:</strong> {{ detailBooking.linked_visit_id }}</div>
          </div>
        </div>

        <div v-if="detailBooking.notes" class="detail-section">
          <h6>Booking Notes</h6>
          <p class="detail-note mb-0">{{ detailBooking.notes }}</p>
        </div>

        <div v-if="detailBooking.rejection_reason" class="detail-section">
          <h6>Rejection Reason</h6>
          <p class="detail-note detail-note-danger mb-0">{{ detailBooking.rejection_reason }}</p>
        </div>
      </div>
      </div>

      <template #modal-footer>
        <div class="booking-detail-footer">
          <b-button type="button" variant="secondary" @click="$bvModal.hide('appointment-booking-detail-modal')">Close</b-button>
          <b-button
            v-if="detailBooking.status === 'pending_approval'"
            type="button"
            variant="danger"
            @click="openRejectModal([detailBooking.id], detailBooking.patient_name)"
          >
            Reject
          </b-button>
          <b-button
            v-if="detailBooking.status === 'pending_approval'"
            type="button"
            variant="success"
            @click="openApproveDialog(detailBooking)"
          >
            Approve
          </b-button>
          <b-button
            v-if="detailBooking.status === 'approved' && !detailBooking.linked_visit_id"
            type="button"
            variant="primary"
            @click="openCheckInDialog(detailBooking)"
          >
            Check In
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-modal id="approve-booking-modal" centered hide-footer title="Approve Appointment" modal-class="bookings-modal-shell">
      <div v-if="approvalDialog.id">
        <p class="mb-2">Approve <strong>{{ approvalDialog.patient_name }}</strong> for <strong>{{ approvalDialog.service_name }}</strong>?</p>
        <div class="choice-card">
          <strong>{{ formatDate(approvalDialog.slot_date) }} | {{ formatTimeRange(approvalDialog.start_time, approvalDialog.end_time) }}</strong>
          <div class="small text-muted mt-1">This confirms the exact booked slot and keeps it reserved for the patient.</div>
        </div>
        <div class="d-flex justify-content-end gap-2 mt-3">
          <b-button type="button" variant="secondary" @click="$bvModal.hide('approve-booking-modal')">Cancel</b-button>
          <b-button type="button" variant="success" :disabled="savingApproval" @click="approveBooking()">
            <b-spinner v-if="savingApproval" small class="align-middle me-1"></b-spinner>
            {{ savingApproval ? "Approving..." : "Approve Appointment" }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <b-modal id="reject-booking-modal" centered hide-footer title="Reject Appointment" modal-class="bookings-modal-shell">
      <form @submit.prevent="rejectBookings">
        <div class="mb-3">
          <p class="mb-2">Reject appointment for <strong>{{ rejectionTargetLabel }}</strong>.</p>
          <label class="form-label">Reason</label>
          <b-form-textarea
            v-model="rejectionReason"
            rows="4"
            placeholder="State the reason for rejecting this appointment"
          ></b-form-textarea>
        </div>
        <div class="d-flex justify-content-end gap-2">
          <b-button type="button" variant="secondary" @click="$bvModal.hide('reject-booking-modal')">Cancel</b-button>
          <b-button type="submit" variant="danger" :disabled="savingRejection">
            <b-spinner v-if="savingRejection" small class="align-middle me-1"></b-spinner>
            {{ savingRejection ? "Rejecting..." : "Reject Appointment" }}
          </b-button>
        </div>
      </form>
    </b-modal>

    <b-modal id="checkin-booking-modal" centered hide-footer title="Check In Appointment" modal-class="bookings-modal-shell">
      <div v-if="checkInDialog.id">
        <p class="mb-2">Create the real Afrimed visit for <strong>{{ checkInDialog.patient_name }}</strong> now?</p>
        <div class="choice-card">
          <strong>{{ checkInDialog.service_name }}</strong>
          <div class="small text-muted mt-1" v-if="checkInDialog.payment_mode === 'pay_in_app'">
            This patient already paid in app, so the visit will be created with payment cleared automatically.
          </div>
          <div class="small text-muted mt-1" v-else>
            This will create the normal visit now, and finance can complete payment afterward as part of the usual pay-on-arrival workflow.
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2 mt-3">
          <b-button type="button" variant="secondary" @click="$bvModal.hide('checkin-booking-modal')">Cancel</b-button>
          <b-button type="button" variant="primary" :disabled="savingCheckIn" @click="checkInBooking()">
            <b-spinner v-if="savingCheckIn" small class="align-middle me-1"></b-spinner>
            {{ savingCheckIn ? "Checking In..." : "Create Visit and Check In" }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </Layout>
</template>

<script>
import axios from "axios";
import Multiselect from "vue-multiselect";
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";

export default {
  page: {
    title: "Patient Appointments",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: { Layout, PageHeader, Multiselect },
  data() {
    return {
      title: "Patient Appointments",
      items: [
        { text: "Dashboard", to: "/" },
        { text: "Patients", to: "/viewpatient" },
        { text: "Appointments", active: true },
      ],
      userData: JSON.parse(localStorage.getItem("user")) || {},
      loading: false,
      loadingDayBookings: false,
      savingApproval: false,
      savingRejection: false,
      savingCheckIn: false,
      activeBookingsTab: 0,
      bookings: [],
      calendarDays: [],
      dayBookings: [],
      pagination: {
        current_page: 1,
        per_page: 8,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0,
        pending_matches: 0,
      },
      summary: {
        total: 0,
        pending_approval: 0,
        approved: 0,
        checked_in: 0,
      },
      searchTerm: "",
      statusFilter: { value: "", label: "All statuses" },
      currentPage: 1,
      perPage: 8,
      searchDebounceId: null,
      realtimeRefreshTimer: null,
      backgroundRefreshInFlight: false,
      queuedRefresh: false,
      currentMonth: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      selectedCalendarDate: "",
      weekDays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      detailBooking: {},
      approvalDialog: {},
      checkInDialog: {},
      rejectionIds: [],
      rejectionReason: "",
      rejectionTargetLabel: "",
      bookingFields: [
        { key: "patient_name", label: "Patient" },
        { key: "service_name", label: "Service" },
        { key: "doctor_name", label: "Doctor" },
        { key: "slot", label: "Slot" },
        { key: "payment", label: "Payment" },
        { key: "status", label: "Status" },
        { key: "actions", label: "Actions" },
      ],
    };
  },
  computed: {
    canManageBookings() {
      return Number(this.userData?.permissions?.appointment_manager || 0) === 1
        || Number(this.userData?.permissions?.patient_appointments || 0) === 1
        || Number(this.userData?.permissions?.app_bookings || 0) === 1;
    },
    statusFilterOptions() {
      return [
        { value: "", label: "All statuses" },
        { value: "pending_approval", label: "Pending Approval" },
        { value: "approved", label: "Approved" },
        { value: "checked_in", label: "Checked In" },
        { value: "rejected", label: "Rejected" },
      ];
    },
    paginationLabel() {
      if (!this.pagination.total) return "0 appointments";
      return `${this.pagination.from}-${this.pagination.to} of ${this.pagination.total} appointments`;
    },
    pendingVisibleCount() {
      return this.pagination.pending_matches || 0;
    },
    summaryCards() {
      return [
        { key: "total", label: "Total Bookings", value: this.summary.total, note: "All booked appointments in this hospital." },
        { key: "pending", label: "Pending Approval", value: this.summary.pending_approval, note: "Manual bookings still waiting for manager action." },
        { key: "approved", label: "Approved", value: this.summary.approved, note: "Confirmed appointments ready to be received into the normal visit flow." },
        { key: "checked_in", label: "Checked In", value: this.summary.checked_in, note: "Appointments already converted into real visits." },
      ];
    },
    todayIso() {
      return this.isoDate(new Date());
    },
    calendarMap() {
      return this.calendarDays.reduce((acc, item) => {
        acc[item.slot_date] = item;
        return acc;
      }, {});
    },
    calendarLabel() {
      return this.currentMonth.toLocaleString(undefined, { month: "long", year: "numeric" });
    },
    selectedCalendarDateLabel() {
      if (!this.selectedCalendarDate) return "Choose a calendar day";
      return this.formatDate(this.selectedCalendarDate);
    },
    selectedDayPendingCount() {
      return this.dayBookings.filter((item) => item.status === "pending_approval").length;
    },
    calendarCells() {
      const start = new Date(this.currentMonth);
      const firstDay = start.getDay();
      start.setDate(1 - firstDay);
      const cells = [];

      for (let index = 0; index < 42; index += 1) {
        const date = new Date(start);
        date.setDate(start.getDate() + index);
        const iso = this.isoDate(date);
        const daySummary = this.calendarMap[iso] || {};
        cells.push({
          key: `${iso}-${index}`,
          date: iso,
          day: date.getDate(),
          inMonth: date.getMonth() === this.currentMonth.getMonth(),
          count: Number(daySummary.total_bookings || 0),
          pending: Number(daySummary.pending_bookings || 0),
        });
      }

      return cells;
    },
  },
  watch: {
    searchTerm() {
      this.scheduleFilterRefresh();
    },
    statusFilter() {
      this.scheduleFilterRefresh();
    },
    currentPage(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.initialLoad({ silent: true });
      }
    },
  },
  mounted() {
    if (this.canManageBookings) {
      this.initialLoad();
      this.subscribeToRealtime();
      this.startBackgroundRefresh();
    }
  },
  beforeDestroy() {
    if (this.searchDebounceId) {
      clearTimeout(this.searchDebounceId);
    }

    if (this.realtimeRefreshTimer) {
      clearInterval(this.realtimeRefreshTimer);
    }

    if (this.userData?.businessID && this.$echo) {
      this.$echo.leave(`business.${this.userData.businessID}.appointments`);
    }
  },
  methods: {
    bookingQueryParams() {
      return {
        page: this.currentPage,
        per_page: this.perPage,
        search: this.searchTerm || undefined,
        status: this.statusFilter?.value || undefined,
      };
    },
    scheduleFilterRefresh() {
      if (this.searchDebounceId) {
        clearTimeout(this.searchDebounceId);
      }

      this.searchDebounceId = setTimeout(() => {
        if (this.currentPage !== 1) {
          this.currentPage = 1;
        } else {
          this.initialLoad({ silent: true });
        }
      }, 260);
    },
    subscribeToRealtime() {
      if (!this.$echo || !this.userData?.businessID) {
        return;
      }

      this.$echo
        .private(`business.${this.userData.businessID}.appointments`)
        .listen(".appointment.module", (event) => {
          if (event?.entity !== "appointment_booking") {
            return;
          }
          this.queueRealtimeRefresh();
        });
    },
    startBackgroundRefresh() {
      this.realtimeRefreshTimer = setInterval(() => {
        this.queueRealtimeRefresh();
      }, 45000);
    },
    async queueRealtimeRefresh() {
      if (this.backgroundRefreshInFlight) {
        this.queuedRefresh = true;
        return;
      }

      this.backgroundRefreshInFlight = true;

      try {
        await this.initialLoad({ silent: true, refreshDay: this.activeBookingsTab === 1 });
      } finally {
        this.backgroundRefreshInFlight = false;

        if (this.queuedRefresh) {
          this.queuedRefresh = false;
          this.queueRealtimeRefresh();
        }
      }
    },
    async initialLoad(options = {}) {
      const { silent = false, refreshDay = false } = options;
      if (!silent) {
        this.loading = true;
      }
      try {
        const response = await axios.get("/appointmentbookingscenter", {
          params: this.bookingQueryParams(),
        });
        this.bookings = response.data?.bookings || [];
        this.calendarDays = response.data?.calendar_days || [];
        this.summary = response.data?.summary || this.summary;
        this.pagination = response.data?.pagination || this.pagination;
        this.currentPage = this.pagination.current_page || this.currentPage;
        this.perPage = this.pagination.per_page || this.perPage;
        if (!this.selectedCalendarDate) {
          await this.selectCalendarDate(this.todayIso, { silent });
        } else if (refreshDay && this.selectedCalendarDate) {
          await this.selectCalendarDate(this.selectedCalendarDate, { silent: true });
        }
      } catch (error) {
        this.showRequestError(error, "Failed to load appointment bookings.");
      } finally {
        if (!silent) {
          this.loading = false;
        }
      }
    },
    openBookingDetail(booking) {
      this.detailBooking = booking || {};
      this.$bvModal.show("appointment-booking-detail-modal");
    },
    openApproveDialog(booking) {
      this.approvalDialog = booking || {};
      this.$bvModal.show("approve-booking-modal");
    },
    openRejectModal(ids, label) {
      this.rejectionIds = ids;
      this.rejectionTargetLabel = label;
      this.rejectionReason = "";
      this.$bvModal.show("reject-booking-modal");
    },
    openCheckInDialog(booking) {
      this.checkInDialog = booking || {};
      this.$bvModal.show("checkin-booking-modal");
    },
    async approveBooking() {
      if (!this.approvalDialog?.id) return;
      this.savingApproval = true;
      try {
        const response = await axios.post("/approveappointmentbookings", {
          ids: [this.approvalDialog.id],
        });
        this.$toast.success(response.data?.message || "Appointment approved successfully.");
        this.$bvModal.hide("approve-booking-modal");
        this.$bvModal.hide("appointment-booking-detail-modal");
        await this.initialLoad();
      } catch (error) {
        this.showRequestError(error, "Failed to approve appointment.");
      } finally {
        this.savingApproval = false;
      }
    },
    async rejectBookings() {
      if (!this.rejectionIds.length) return;
      if (!this.rejectionReason.trim()) {
        this.$toast.warning("Provide a reason before rejecting this appointment.");
        return;
      }
      this.savingRejection = true;
      try {
        const response = await axios.post("/rejectappointmentbookings", {
          ids: this.rejectionIds,
          reason: this.rejectionReason,
        });
        this.$toast.success(response.data?.message || "Appointment rejected successfully.");
        this.$bvModal.hide("reject-booking-modal");
        this.$bvModal.hide("appointment-booking-detail-modal");
        await this.initialLoad();
      } catch (error) {
        this.showRequestError(error, "Failed to reject appointment.");
      } finally {
        this.savingRejection = false;
      }
    },
    async checkInBooking() {
      if (!this.checkInDialog?.id) return;
      this.savingCheckIn = true;
      try {
        const response = await axios.post("/checkinappointmentbooking", {
          id: this.checkInDialog.id,
        });
        const visitId = response.data?.visit_id;
        const message = visitId
          ? `${response.data?.message || "Appointment checked in successfully."} Visit ${visitId} was created.`
          : (response.data?.message || "Appointment checked in successfully.");
        this.$toast.success(message);
        this.$bvModal.hide("checkin-booking-modal");
        this.$bvModal.hide("appointment-booking-detail-modal");
        await this.initialLoad();
      } catch (error) {
        this.showRequestError(error, "Failed to check in this appointment.");
      } finally {
        this.savingCheckIn = false;
      }
    },
    async selectCalendarDate(date, options = {}) {
      const { silent = false } = options;
      this.selectedCalendarDate = date;
      if (!silent) {
        this.loadingDayBookings = true;
      }
      try {
        const response = await axios.get("/appointmentbookingday", { params: { slot_date: date } });
        this.dayBookings = response.data?.bookings || [];
      } catch (error) {
        this.showRequestError(error, "Failed to load the selected day appointments.");
      } finally {
        if (!silent) {
          this.loadingDayBookings = false;
        }
      }
    },
    shiftMonth(step) {
      const next = new Date(this.currentMonth);
      next.setMonth(next.getMonth() + step);
      this.currentMonth = new Date(next.getFullYear(), next.getMonth(), 1);
    },
    isoDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    formatDateTime(value) {
      if (!value) return "";
      return new Date(value).toLocaleString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    },
    formatTime(value) {
      if (!value) return "";
      const [hours, minutes] = String(value).split(":").map(Number);
      const date = new Date();
      date.setHours(hours || 0, minutes || 0, 0, 0);
      return date.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      });
    },
    formatTimeRange(start, end) {
      return `${this.formatTime(start)} - ${this.formatTime(end)}`;
    },
    formatStatus(value) {
      return String(value || "").replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    formatPaymentMode(value) {
      if (value === "pay_in_app") return "Pay In App";
      if (value === "pay_on_arrival") return "Pay On Arrival";
      return this.formatStatus(value);
    },
    statusBadgeClass(status) {
      if (status === "approved" || status === "checked_in") return "bg-soft-success text-success";
      if (status === "rejected" || status === "cancelled") return "bg-soft-danger text-danger";
      return "bg-soft-warning text-warning";
    },
    money(value) {
      return Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    showRequestError(error, fallbackMessage) {
      const message = error?.response?.data?.message || Object.values(error?.response?.data?.errors || {})?.[0]?.[0] || fallbackMessage;
      this.$toast.error(message);
    },
  },
};
</script>

<style scoped>
.bookings-tabs {
  gap: 0.75rem;
  border-bottom: 0;
}

.bookings-tabs .nav-item {
  margin-bottom: 0;
}

.bookings-tabs .nav-link {
  border: 1px solid #d9e3ee;
  border-radius: 999px;
  color: #52637a;
  background: #fff;
  padding: 0.7rem 1.15rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.bookings-tabs .nav-link.active {
  color: #fff;
  background: linear-gradient(135deg, #03967d 0%, #14b8a6 100%);
  border-color: transparent;
  box-shadow: 0 10px 26px rgba(3, 150, 125, 0.22);
}

.summary-card,
.bookings-review-card,
.calendar-shell-card {
  border-radius: 1rem;
}

.summary-card {
  border: 1px solid #e2e8f0;
  background: linear-gradient(180deg, #ffffff 0%, #fbfffe 100%);
}

.summary-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 0.45rem;
}

.summary-value {
  font-size: 1.9rem;
  line-height: 1;
  font-weight: 700;
  color: #0f172a;
}

.summary-note {
  margin-top: 0.45rem;
  color: #64748b;
  font-size: 0.9rem;
}

.booking-tools {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.booking-search {
  width: 320px;
  min-width: 320px;
}

.booking-status-select {
  width: 220px;
  min-width: 220px;
}

.pending-pill {
  font-size: 0.85rem;
}

.bookings-state {
  min-height: 220px;
  border: 1px dashed #d8e3ef;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  background: #fbfdff;
  text-align: center;
  padding: 1rem;
}

.bookings-table-shell {
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  overflow: hidden;
  background: #fff;
}

.booking-row {
  transition: background 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
}

.booking-row:hover {
  background: #fbfefd;
  box-shadow: inset 0 0 0 1px rgba(3, 150, 125, 0.08);
}

.booking-primary-cell,
.booking-slot-cell,
.booking-status-cell {
  display: grid;
  gap: 0.2rem;
}

.booking-link-text {
  color: #0f766e;
  text-decoration: underline;
  text-decoration-thickness: 1.5px;
  text-underline-offset: 0.18em;
  text-decoration-color: rgba(15, 118, 110, 0.4);
}

.booking-primary-cell span,
.booking-slot-cell span {
  font-size: 0.84rem;
  color: #64748b;
}

.booking-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.table-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  flex-wrap: wrap;
}

:deep(.bookings-head th) {
  padding: 0.95rem 1rem;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  background: #f8fbfc;
  border-bottom: 1px solid #e2e8f0;
}

.calendar-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.calendar-shell {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1rem;
  align-items: stretch;
}

.calendar-pane,
.slots-pane {
  min-height: 560px;
  max-height: 560px;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  background: #fff;
  padding: 1rem;
}

.calendar-pane {
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.55rem;
}

.calendar-head {
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.calendar-cell {
  min-height: 78px;
  border: 1px solid #dbe4f0;
  border-radius: 0.85rem;
  background: #fff;
  padding: 0.55rem;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  position: relative;
}

.calendar-cell.active {
  border-color: #03967d;
  box-shadow: 0 0 0 1px rgba(3, 150, 125, 0.25);
}

.calendar-cell.touched {
  border-color: rgba(3, 150, 125, 0.35);
  background: rgba(3, 150, 125, 0.06);
}

.calendar-cell.booked {
  box-shadow: inset 0 0 0 1px rgba(220, 38, 38, 0.16);
}

.calendar-cell.today:not(.active) {
  border-color: rgba(3, 150, 125, 0.42);
  background: linear-gradient(180deg, rgba(240, 253, 250, 0.96), rgba(255, 255, 255, 0.98));
  box-shadow: inset 0 0 0 1px rgba(3, 150, 125, 0.12);
}

.calendar-cell.today .day-number {
  color: #047857;
}

.calendar-cell.muted {
  opacity: 0.45;
}

.day-number {
  font-weight: 700;
  color: #1e293b;
}

.calendar-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.75rem;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  background: #03967d;
  color: #fff;
  font-size: 0.72rem;
}

.calendar-booked-dot {
  position: absolute;
  top: 0.7rem;
  right: 0.7rem;
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 999px;
  background: #dc2626;
  box-shadow: 0 0 0 3px rgba(254, 226, 226, 0.95);
}

.slots-pane {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.slots-pane-head {
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.9rem;
}

.slots-state {
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #0f766e;
  border: 1px dashed #dbe4f0;
  border-radius: 0.9rem;
  padding: 1rem;
  background: #fbfdff;
}

.slots-state.empty {
  color: #64748b;
}

.slot-list {
  display: grid;
  gap: 0.75rem;
  overflow-y: auto;
  min-height: 0;
  padding-right: 0.25rem;
}

.slot-list::-webkit-scrollbar,
:deep(.bookings-modal-shell .modal-body::-webkit-scrollbar),
.booking-detail-scroll::-webkit-scrollbar {
  width: 6px;
}

.slot-list::-webkit-scrollbar-track,
:deep(.bookings-modal-shell .modal-body::-webkit-scrollbar-track),
.booking-detail-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.slot-list::-webkit-scrollbar-thumb,
:deep(.bookings-modal-shell .modal-body::-webkit-scrollbar-thumb),
.booking-detail-scroll::-webkit-scrollbar-thumb {
  background: rgba(100, 116, 139, 0.16);
  border-radius: 999px;
}

.slot-card {
  border: 1px solid #e2e8f0;
  border-radius: 0.85rem;
  padding: 0.85rem;
  background: #fff;
  width: 100%;
  text-align: left;
}

.booking-card-button {
  transition: background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.booking-card-button:hover {
  border-color: rgba(3, 150, 125, 0.28);
  box-shadow: inset 0 0 0 1px rgba(3, 150, 125, 0.08);
}

:deep(.bookings-modal-shell .modal-dialog) {
  display: flex;
  align-items: center;
  min-height: calc(100vh - 4.5rem);
  margin-top: 2.25rem;
  margin-bottom: 2.25rem;
}

:deep(.bookings-modal-shell .modal-content) {
  max-height: calc(100vh - 4.5rem);
  display: flex;
  flex-direction: column;
  border-radius: 1rem;
  overflow: hidden;
}

:deep(.bookings-modal-shell .modal-header),
:deep(.bookings-modal-shell .modal-footer) {
  flex: 0 0 auto;
}

:deep(.bookings-modal-shell .modal-body) {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

:deep(.booking-detail-shell .modal-dialog) {
  max-width: 1160px;
  width: min(1160px, calc(100vw - 2rem));
  margin: 1rem auto;
}

:deep(.booking-detail-shell .modal-content) {
  height: min(820px, calc(100vh - 2rem));
  max-height: min(820px, calc(100vh - 2rem));
}

:deep(.booking-detail-shell .modal-body) {
  padding: 0;
}

.booking-detail-body {
  display: flex;
  min-height: 0;
}

.booking-detail-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1.5rem;
}

:deep(.booking-detail-shell .modal-footer) {
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid #edf2f7;
  background: #fff;
}

.booking-detail-modal {
  display: grid;
  gap: 1rem;
}

.booking-detail-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.detail-eyebrow {
  display: inline-block;
  margin-bottom: 0.35rem;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #0f766e;
}

.booking-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
}

.detail-stat,
.choice-card {
  border: 1px solid #dbe4f0;
  border-radius: 0.95rem;
  padding: 0.95rem 1rem;
  background: #fbfdff;
}

.detail-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  margin-bottom: 0.35rem;
}

.detail-value {
  color: #0f172a;
  font-weight: 600;
}

.detail-section {
  border-top: 1px solid #edf2f7;
  padding-top: 1rem;
}

.detail-copy {
  display: grid;
  gap: 0.45rem;
  color: #475569;
}

.detail-note {
  padding: 0.85rem 1rem;
  border-radius: 0.9rem;
  background: #f8fafc;
  color: #334155;
}

.detail-note-danger {
  background: #fef2f2;
  color: #b91c1c;
}

.booking-detail-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
  width: 100%;
}

@media (max-width: 991.98px) {
  .calendar-shell {
    grid-template-columns: 1fr;
  }

  .calendar-pane,
  .slots-pane {
    min-height: 420px;
    max-height: none;
  }

  .slot-list {
    max-height: 420px;
  }
}

@media (max-width: 767.98px) {
  .booking-detail-grid {
    grid-template-columns: 1fr;
  }

  .booking-tools {
    width: 100%;
    justify-content: flex-start;
  }

  .booking-search,
  .booking-status-select {
    width: 100%;
    min-width: 0;
  }

  .table-footer {
    justify-content: center;
  }
}
</style>
